import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import SearchResults from "_shared/_api/search/search-results";
import { assertIsAxiosError } from "_shared/_errors/errors";
import Guid from "_shared/guids/guid";
import axios, { AxiosHeaders, AxiosResponse, CanceledError, HttpStatusCode } from "axios";
import MarketplaceBasicUserInfoAPIResponse from "marketplace/_api/response-contracts/marketplace-basic-user-info-api-response";
import MarketplaceIndividualSearch from "marketplace/individuals/_api/commands/marketplace-individual-search";
import MarketplaceIndividualAPIResponse from "marketplace/individuals/_api/response-contracts/marketplace-individual-api-response";
import IndividualSchema, { IndividualJSON } from "marketplace/individuals/_schemas/individual-schema";
import UpdateMarketplaceIndividualProfileAPIRequest from "marketplace/individuals/profiles/_api/request-contracts/update-marketplace-individual-profile-api-request";
import MarketplaceIndividualInfoAPIResponse from "marketplace/individuals/profiles/_api/response-contracts/marketplace-individual-info-api-response";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import IndividualSummariesAPIResponse from "marketplace/individuals/profiles/summaries/_api/responses/individual-summaries-api-response";
import IndividualSummaryAPIResponse from "marketplace/individuals/profiles/summaries/_api/responses/individual-summary-api-response";
import IndividualSummary from "marketplace/individuals/profiles/summaries/individual-summary";
import ForumSchema, { ForumJSON } from "messaging/forums/_schemas/forum-schema";
import Session from "users/__session/session";

export default class IndividualAPIService {
  public constructor(session: Readonly<Session>) {
    if (!session.authToken) throw new Error("Cannot create IndividualAPIService without session.");
    this.headers.set("Authorization", `Bearer ${session.authToken.value}`);
  }

  public async getIndividualVendors(abortController?: AbortController): Promise<IndividualJSON[]> {
    const url = new URL("/marketplace/individual-vendors", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: abortController?.signal,
      });
      return IndividualSchema.validateJSONArray(response.data);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      throw new IndividualServiceError("getIndividualVendors", error);
    }
  }

  public async getIndividualById(individualId: Guid, abortController?: AbortController): Promise<IndividualJSON> {
    const url = new URL(`/marketplace/individuals/${individualId.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: abortController?.signal,
      });
      return IndividualSchema.validateJSON(response.data);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      assertIsAxiosError(error);
      switch (error.response?.status) {
        case HttpStatusCode.BadRequest:
          throw new IndividualHiddenError(String(error.response.data));
        case HttpStatusCode.NotFound:
          throw new IndividualNotFoundError(String(error.response.data));
        default:
          throw new IndividualServiceError("getIndividualById", error);
      }
    }
  }

  public async getMarketplaceIndividualById(
    individualId: Guid,
    abortController?: AbortController,
  ): Promise<MarketplaceIndividualInfoAPIResponse> {
    const url = new URL(`/marketplace/individuals/${individualId.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: abortController?.signal,
      });
      return Object.assign(new MarketplaceIndividualInfoAPIResponse(), response.data);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      assertIsAxiosError(error);
      switch (error.response?.status) {
        case HttpStatusCode.BadRequest:
          throw new IndividualHiddenError(String(error.response.data));
        case HttpStatusCode.NotFound:
          throw new IndividualNotFoundError(String(error.response.data));
        default:
          throw new IndividualServiceError("getIndividualById", error);
      }
    }
  }

  public async getIndividualsByIds(ids: Guid[], abortController?: AbortController): Promise<IndividualJSON[]> {
    if (ids.length < 1) {
      console.warn("getIndividualsByIds called with empty ids array.");
      return [];
    }
    const url = new URL("/marketplace/individuals-by-ids", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(
        url.toString(),
        ids.map((id) => id.value),
        {
          headers: this.headers.concat({ "Content-Type": "application/json-patch+json" }),
          signal: abortController?.signal,
        },
      );
      return IndividualSchema.validateJSONArray(response.data);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      assertIsAxiosError(error);
      throw new IndividualServiceError("getIndividualsByIds", error);
    }
  }

  public async getIndividualsByUserIds(userIds: Guid[], abortController?: AbortController): Promise<IndividualJSON[]> {
    if (userIds.length < 1) {
      console.warn("getIndividualsByUserIds called with empty userIds array.");
      return [];
    }
    const url = new URL("/marketplace/individuals-by-userids", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(
        url.toString(),
        userIds.map((id) => id.value),
        {
          headers: this.headers.concat({ "Content-Type": "application/json-patch+json" }),
          signal: abortController?.signal,
        },
      );
      return IndividualSchema.validateJSONArray(response.data);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      assertIsAxiosError(error);
      throw new IndividualServiceError("getIndividualsByUserIds", error);
    }
  }

  public async getIndividualsFromEntityId(
    entityId: Guid,
    abortController: AbortController,
  ): Promise<IndividualSummary[]> {
    const url = new URL(`/marketplace/individuals/entity/${entityId.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: abortController.signal,
      });
      const responseData = Object.assign(new IndividualSummariesAPIResponse(), {
        individuals: response.data.individuals.map((individual: Partial<MarketplaceBasicUserInfoAPIResponse>) =>
          Object.assign(new IndividualSummaryAPIResponse(), individual),
        ),
      });
      return responseData.deserialize();
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound) {
        throw new EntityIndividualsNotFoundError(String(error.response.data));
      }
      throw new IndividualServiceError("getIndividualsFromEntityId", error);
    }
  }

  public async searchIndividuals(
    search: MarketplaceIndividualSearch,
    abortController: AbortController,
    limitToVendorReps: boolean = false,
  ): Promise<SearchResults<MarketplaceIndividualAPIResponse>> {
    let params = new URLSearchParams();
    for (const [key, value] of Object.entries(search)) {
      if (value) {
        params.append(key, value.toString());
      }
    }
    params.append("limitToVendorReps", limitToVendorReps.toString());
    const url = new URL(`/marketplace/individuals?${params.toString()}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: abortController.signal,
      });
      return new SearchResults<MarketplaceIndividualAPIResponse>(response.data);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      assertIsAxiosError(error);
      if (error.response?.data) {
        throw new IndividualSearchError(JSON.stringify(error.response.data));
      } else {
        throw error;
      }
    }
  }

  public async getIndividualAvatar(individualId: Guid, abortController?: AbortController): Promise<string> {
    const url = new URL(`/marketplace/individuals/${individualId.value}/avatar`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: abortController?.signal,
      });
      return response.data;
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound) {
        throw new IndividualAvatarNotFoundError(String(error.response.data));
      }
      throw new IndividualServiceError("getIndividualAvatar", error);
    }
  }

  public async downloadIndividualResume(individualId: Guid, abortController?: AbortController): Promise<AxiosResponse> {
    const url = new URL(`/marketplace/individuals/${individualId.value}/resume`, AttorneyHubAPIService.apiBaseUrl);
    try {
      return await axios.get(url.toString(), {
        headers: this.headers,
        signal: abortController?.signal,
        responseType: "blob",
      });
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound) {
        throw new IndividualResumeNotFoundError(String(error.response.data));
      }
      throw new IndividualServiceError("downloadIndividualResume", error);
    }
  }

  public async updateIndividual(
    individualId: Guid,
    isVisible: boolean,
    originalProfile?: IndividualProfile,
    updatedProfile?: IndividualProfile,
  ): Promise<IndividualJSON> {
    const url = new URL(`/marketplace/individuals/${individualId.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.patch(
        url.toString(),
        new UpdateMarketplaceIndividualProfileAPIRequest(isVisible, originalProfile, updatedProfile).payload,
        {
          headers: this.headers.concat({ "Content-Type": "application/json-patch+json" }),
        },
      );
      return response.data;
    } catch (error) {
      assertIsAxiosError(error);
      switch (error.response?.status) {
        case HttpStatusCode.BadRequest:
          throw new IndividualUpdateError(String(error.response.data));
        case HttpStatusCode.NotFound:
          throw new IndividualNotFoundError(String(error.response.data));
        default:
          throw new IndividualServiceError("updateIndividualProfile", error);
      }
    }
  }

  public async createIndividualAvatar(individualId: Guid, file: File): Promise<void> {
    const url = new URL(`/marketplace/individuals/${individualId.value}/avatar`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(url.toString(), formData, {
        headers: this.headers.concat({ "Content-Type": "multipart/form-data" }),
      });
    } catch (error) {
      assertIsAxiosError(error);
      switch (error.response?.status) {
        case HttpStatusCode.NotFound:
          throw new IndividualNotFoundError(String(error.response.data));
        case HttpStatusCode.UnprocessableEntity:
          throw new IndividualAvatarError(String(error.response.data));
        default:
          throw new IndividualServiceError("createIndividualAvatar", error);
      }
    }
  }

  public async createIndividualResume(individualId: Guid, file: File): Promise<void> {
    const url = new URL(`/marketplace/individuals/${individualId.value}/resume`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(url.toString(), formData, {
        headers: this.headers.concat({ "Content-Type": "multipart/form-data" }),
      });
    } catch (error) {
      assertIsAxiosError(error);
      switch (error.response?.status) {
        case HttpStatusCode.NotFound:
          throw new IndividualNotFoundError(String(error.response.data));
        case HttpStatusCode.UnprocessableEntity:
          throw new IndividualResumeError(String(error.response.data));
        default:
          throw new IndividualServiceError("createIndividualResume", error);
      }
    }
  }

  public async deleteIndividualAvatar(individualId: Guid): Promise<void> {
    const url = new URL(`/marketplace/individuals/${individualId.value}/avatar`, AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.delete(url.toString(), {
        headers: this.headers,
      });
    } catch (error) {
      assertIsAxiosError(error);
      switch (error.response?.status) {
        case HttpStatusCode.NotFound:
          throw new IndividualNotFoundError(String(error.response.data));
        case HttpStatusCode.UnprocessableEntity:
          throw new IndividualAvatarError(String(error.response.data));
        default:
          throw new IndividualServiceError("deleteIndividualAvatar", error);
      }
    }
  }

  public async deleteIndividualResume(individualId: Guid): Promise<void> {
    const url = new URL(`/marketplace/individuals/${individualId.value}/resume`, AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.delete(url.toString(), {
        headers: this.headers,
      });
    } catch (error) {
      assertIsAxiosError(error);
      switch (error.response?.status) {
        case HttpStatusCode.NotFound:
          throw new IndividualNotFoundError(String(error.response.data));
        case HttpStatusCode.UnprocessableEntity:
          throw new IndividualResumeError(String(error.response.data));
        default:
          throw new IndividualServiceError("deleteIndividualResume", error);
      }
    }
  }

  public async contactIndividual(individualId: Guid): Promise<ForumJSON> {
    const url = new URL(`/marketplace/individuals/${individualId.value}/contact`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(
        url.toString(),
        {},
        {
          headers: this.headers,
        },
      );
      return ForumSchema.validateJSON(response.data);
    } catch (error) {
      assertIsAxiosError(error);
      switch (error.response?.status) {
        case HttpStatusCode.BadRequest:
          throw new IndividualContactError(String(error.response.data));
        case HttpStatusCode.NotFound:
          throw new IndividualNotFoundError(String(error.response.data));
        default:
          throw error;
      }
    }
  }

  private readonly headers: AxiosHeaders = new AxiosHeaders();
}

export class IndividualServiceError extends Error {
  method: string;
  error: unknown;
  constructor(method: string, error: unknown) {
    super(`Error in IndividualService.${method}: ${error}`);
    this.method = method;
    this.error = error;
  }
}

export class IndividualSearchError extends Error {}
export class IndividualNotFoundError extends Error {}
export class IndividualHiddenError extends Error {}
export class EntityIndividualsNotFoundError extends Error {}
export class IndividualCreationError extends Error {}
export class IndividualUpdateError extends Error {}
export class IndividualAvatarError extends Error {}
export class IndividualAvatarNotFoundError extends Error {}
export class IndividualResumeError extends Error {}
export class IndividualResumeNotFoundError extends Error {}
export class IndividualContactError extends Error {}
